import {
  initializeApp
} from 'https://www.gstatic.com/firebasejs/10.13.0/firebase-app.js';
import {
  getFirestore,
  collection,
  addDoc,
  doc,
  updateDoc,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp,
  arrayUnion,
  arrayRemove
} from 'https://www.gstatic.com/firebasejs/10.13.0/firebase-firestore.js';

/* ──────────────────────────────────────────
   Helpers
────────────────────────────────────────── */
function escapeHtml(str) {
  const div = document.createElement('div');
  div.textContent = str == null ? '' : String(str);
  return div.innerHTML;
}

function initials(name) {
  if (!name) return 'U';
  const parts = name.trim().split(/\s+/);
  const a = parts[0] ? parts[0][0] : '';
  const b = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (a + b).toUpperCase() || 'U';
}

function avatarColorIndex(name) {
  const s = name || 'U';
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 997;
  return (Math.abs(h) % 6) + 1;
}

function avatarEl(name, size = 32) {
  const el = document.createElement('span');
  el.className = 'forum-avatar';
  el.style.cssText = `width:${size}px;height:${size}px;font-size:${Math.round(size * 0.4)}px`;
  el.dataset.c = String(avatarColorIndex(name));
  el.textContent = initials(name);
  el.setAttribute('aria-hidden', 'true');
  return el;
}

function timeAgo(date) {
  if (!date) return '';
  const sec = Math.floor((Date.now() - date.getTime()) / 1000);
  const steps = [['y', 31536000], ['mo', 2592000], ['d', 86400], ['h', 3600], ['m', 60]];
  for (const [label, s] of steps) {
    const v = Math.floor(sec / s);
    if (v >= 1) return `${v}${label} ago`;
  }
  return 'just now';
}

function tokenize(str) {
  return (str || '').toLowerCase().replace(/[^a-z0-9\s]/g, ' ').split(/\s+/).filter(Boolean);
}

function relevanceScore(queryTokens, text) {
  const lower = (text || '').toLowerCase();
  if (!queryTokens.length) return 1;
  const full = queryTokens.join(' ');
  let score = 0;
  if (lower.includes(full)) score += 100;
  const toks = new Set(tokenize(text));
  queryTokens.forEach((qt) => {
    if (toks.has(qt)) {
      score += 10;
    } else {
      for (const tt of toks) {
        if (tt.includes(qt) || qt.includes(tt)) { score += 3; break; }
      }
    }
  });
  return score;
}

/* ──────────────────────────────────────────
   Friendly error messages for Firestore errors.
   Surfacing the real `err.code` instead of failing
   silently is what makes "other users can't answer/
   vote" bugs diagnosable instead of invisible.
────────────────────────────────────────── */
function firestoreErrorMessage(err, fallback) {
  const code = err && err.code;
  if (code === 'permission-denied') {
    return "Couldn't save — check Firestore security rules";
  }
  if (code === 'not-found') {
    return 'This post no longer exists';
  }
  if (code === 'unavailable') {
    return 'Connection issue, please try again';
  }
  return fallback;
}

/* ──────────────────────────────────────────
   Per-section initialisation
────────────────────────────────────────── */
document.querySelectorAll('[id^="forum-config-"]').forEach((configEl) => {
  let cfg;
  try { cfg = JSON.parse(configEl.textContent); } catch { return; }

  const { sectionId, firebaseConfig, customer, loginUrl, collectionName } = cfg;

  // DOM refs
  const $ = (id) => document.getElementById(id);
  const listEl          = $(`forum-list-${sectionId}`);
  const categoriesNav   = $(`forum-categories-${sectionId}`);
  const activeCatBar    = $(`forum-active-cat-${sectionId}`);
  const statQuestions   = $(`forum-stat-questions-${sectionId}`);
  const statAnswers     = $(`forum-stat-answers-${sectionId}`);
  const statMyQuestions = $(`forum-stat-my-questions-${sectionId}`);
  const statMyAnswers   = $(`forum-stat-my-answers-${sectionId}`);
  const myQuestionsList = $(`forum-my-questions-list-${sectionId}`);
  const myAnswersList   = $(`forum-my-answers-list-${sectionId}`);
  const myActivityViewAllBtn = $(`forum-my-activity-viewall-${sectionId}`);
  const myActivityModal       = $(`forum-activity-modal-${sectionId}`);
  const myActivityModalClose  = $(`forum-activity-modal-close-${sectionId}`);
  const myActivityModalBackdrop = $(`forum-activity-modal-backdrop-${sectionId}`);
  const myQuestionsListFull = $(`forum-my-questions-list-full-${sectionId}`);
  const myAnswersListFull   = $(`forum-my-answers-list-full-${sectionId}`);

  const MY_ACTIVITY_SIDEBAR_LIMIT = 5;

  function openActivityModal() {
    if (myActivityModal) myActivityModal.hidden = false;
  }
  function closeActivityModal() {
    if (myActivityModal) myActivityModal.hidden = true;
  }
  myActivityModalClose && myActivityModalClose.addEventListener('click', closeActivityModal);
  myActivityModalBackdrop && myActivityModalBackdrop.addEventListener('click', closeActivityModal);
  const searchInput     = $(`forum-search-${sectionId}`);
  const searchEmptyEl   = $(`forum-search-empty-${sectionId}`);
  const toastEl         = $(`forum-toast-${sectionId}`);

  // Modal refs (only when customer logged in)
  const modalEl         = $(`forum-modal-${sectionId}`);
  const modalBackdrop   = $(`forum-modal-backdrop-${sectionId}`);
  const modalClose      = $(`forum-modal-close-${sectionId}`);
  const modalCancel     = $(`forum-modal-cancel-${sectionId}`);
  const askForm         = $(`forum-ask-form-${sectionId}`);
  const askOpenBtn      = $(`forum-ask-open-${sectionId}`);
  const askTitleInput   = $(`forum-ask-title-${sectionId}`);
  const askBodyInput    = $(`forum-ask-text-${sectionId}`);
  const categorySelect  = $(`forum-category-select-${sectionId}`);
  const askSubmitBtn    = $(`forum-ask-submit-${sectionId}`);
  const charNumEl       = $(`forum-char-num-${sectionId}`);
  const sectionRoot     = $(`forum-section-${sectionId}`);

  // State
  let activeCategory = 'all';
  let activeSort = 'newest';
  let unansweredOnly = false;
  const questionRecords = new Map(); // id -> { el, text, categoryId, score, answerCount }
  const answerCounts    = new Map();
  const myAnswerCounts  = new Map(); // questionId -> count of replies authored by current customer

  /* ── Toast ── */
  let toastTimer;
  function showToast(msg) {
    if (!toastEl) return;
    toastEl.textContent = msg;
    toastEl.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.classList.remove('is-visible'), 2400);
  }

  /* ── Firebase guard ── */
  if (!firebaseConfig || !firebaseConfig.apiKey || !firebaseConfig.projectId) {
    if (listEl) listEl.innerHTML = `<div class="forum-empty-state">Forum is not configured yet. Add your Firebase project keys in the section settings.</div>`;
    return;
  }

  const app = initializeApp(firebaseConfig, `forum-${sectionId}`);
  const db  = getFirestore(app);

  function goToLogin() { window.location.href = loginUrl; }

  /* ── Modal open / close ── */
  function openModal() {
    if (!modalEl) return;
    modalEl.hidden = false;
    document.body.style.overflow = 'hidden';
    if (askTitleInput) askTitleInput.focus();
    // Pre-select category if one is active
    if (categorySelect && activeCategory !== 'all') {
      const opt = Array.from(categorySelect.options).find(o => o.value === activeCategory);
      if (opt) categorySelect.value = activeCategory;
    }
    updateSubmitState();
  }

  function closeModal() {
    if (!modalEl) return;
    modalEl.hidden = true;
    document.body.style.overflow = '';
    if (askForm) askForm.reset();
    if (charNumEl) charNumEl.textContent = '0';
    updateSubmitState();
  }

  if (askOpenBtn) askOpenBtn.addEventListener('click', () => { if (!customer.loggedIn) { goToLogin(); return; } openModal(); });
  if (modalClose) modalClose.addEventListener('click', closeModal);
  if (modalCancel) modalCancel.addEventListener('click', closeModal);
  if (modalBackdrop) modalBackdrop.addEventListener('click', closeModal);

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modalEl && !modalEl.hidden) closeModal();
  });

  /* ── Submit state ── */
  function updateSubmitState() {
    if (!askSubmitBtn) return;
    const hasTitle    = askTitleInput && askTitleInput.value.trim().length > 2;
    const hasCategory = categorySelect && categorySelect.value !== '';
    askSubmitBtn.disabled = !(hasTitle && hasCategory);
  }

  if (askTitleInput) {
    askTitleInput.addEventListener('input', updateSubmitState);
  }
  if (categorySelect) {
    categorySelect.addEventListener('change', updateSubmitState);
  }
  if (askBodyInput) {
    askBodyInput.addEventListener('input', () => {
      const len = askBodyInput.value.length;
      if (charNumEl) charNumEl.textContent = String(len);
      const charWrap = charNumEl && charNumEl.closest('.forum-char-count');
      if (charWrap) charWrap.classList.toggle('is-near-limit', len > 1800);
    });
  }

  /* ── Submit question ── */
  if (askForm) {
    askForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (!customer.loggedIn) { goToLogin(); return; }

      const title = askTitleInput ? askTitleInput.value.trim() : '';
      const body  = askBodyInput  ? askBodyInput.value.trim()  : '';

      if (title.length < 3) {
        askTitleInput && askTitleInput.focus();
        return;
      }
      if (categorySelect && !categorySelect.value) {
        categorySelect.focus();
        return;
      }

      askSubmitBtn.disabled = true;
      try {
        const payload = {
          title,
          text: body || title,       // body falls back to title for backward compat search
          authorId:   customer.id,
          authorName: customer.name || 'Customer',
          createdAt:  serverTimestamp(),
          likes:    [],
          dislikes: []
        };

        if (categorySelect && categorySelect.value) {
          const opt = categorySelect.options[categorySelect.selectedIndex];
          payload.categoryId    = categorySelect.value;
          payload.categoryLabel = opt ? (opt.dataset.label || opt.textContent.trim()) : '';
          payload.categoryIcon  = opt ? (opt.dataset.icon  || '') : '';
        }

        await addDoc(collection(db, collectionName), payload);
        closeModal();
        showToast('Your question was posted');
      } catch (err) {
        console.error('Forum post failed', err);
        showToast(firestoreErrorMessage(err, 'Failed to post. Please try again.'));
      } finally {
        if (askSubmitBtn) askSubmitBtn.disabled = false;
      }
    });
  }

  /* ── Category selection ── */
  function setActiveCategory(catId, label) {
    activeCategory = catId;
    categoriesNav && categoriesNav.querySelectorAll('.forum-cat-btn').forEach((btn) => {
      btn.classList.toggle('is-active', btn.dataset.category === catId);
    });
    if (activeCatBar) {
      activeCatBar.hidden = catId === 'all';
      const labelEl = activeCatBar.querySelector('.forum-active-cat-label');
      if (labelEl) labelEl.textContent = catId === 'all' ? '' : `Viewing: ${label}`;
    }
    applyFilters();
  }

  categoriesNav && categoriesNav.querySelectorAll('.forum-cat-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const catId = btn.dataset.category;
      const labelEl = btn.querySelector('.forum-cat-label');
      setActiveCategory(catId, labelEl ? labelEl.textContent.trim() : '');
    });
  });

  if (activeCatBar) {
    const clearBtn = activeCatBar.querySelector('.forum-active-cat-clear');
    clearBtn && clearBtn.addEventListener('click', () => setActiveCategory('all', ''));
  }

  /* ── Sort ── */
  if (sectionRoot) {
    sectionRoot.querySelectorAll('.forum-sort-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        sectionRoot.querySelectorAll('.forum-sort-btn').forEach(b => b.classList.remove('is-active'));
        btn.classList.add('is-active');
        activeSort = btn.dataset.sort;
        unansweredOnly = activeSort === 'unanswered';
        applyFilters();
      });
    });
  }

  /* ── Search ── */
  let searchDebounce;
  searchInput && searchInput.addEventListener('input', () => {
    clearTimeout(searchDebounce);
    searchDebounce = setTimeout(applyFilters, 150);
  });

  /* ── Filter / sort / render order ── */
  function applyFilters() {
    const raw = searchInput ? searchInput.value.trim() : '';
    const queryTokens = tokenize(raw);
    let visibleCount = 0;

    const scored = [];
    questionRecords.forEach((rec, id) => {
      const matchesCat  = activeCategory === 'all' || rec.categoryId === activeCategory;
      const matchesUnansw = !unansweredOnly || (answerCounts.get(id) || 0) === 0;
      const score = queryTokens.length === 0 ? 1 : relevanceScore(queryTokens, rec.searchText);
      scored.push({ id, rec, score, visible: matchesCat && matchesUnansw && score > 0 });
    });

    // Sort
    if (activeSort === 'top') {
      scored.sort((a, b) => (b.rec.voteScore || 0) - (a.rec.voteScore || 0));
    } else if (activeSort === 'newest' || activeSort === 'unanswered') {
      scored.sort((a, b) => (b.rec.createdMs || 0) - (a.rec.createdMs || 0));
    }
    if (queryTokens.length > 0) {
      scored.sort((a, b) => b.score - a.score);
    }

    scored.forEach(({ rec, visible }, index) => {
      rec.el.hidden = !visible;
      rec.el.style.order = String(index);
      if (visible) visibleCount++;
    });

    if (searchEmptyEl) searchEmptyEl.hidden = visibleCount > 0;
  }

  /* ── Category counts ── */
  function updateCategoryCounts() {
    const counts = new Map();
    questionRecords.forEach((rec) => {
      const key = rec.categoryId || 'uncategorized';
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    categoriesNav && categoriesNav.querySelectorAll('.forum-cat-count').forEach((el) => {
      const key = el.dataset.countFor;
      el.textContent = key === 'all' ? String(questionRecords.size) : String(counts.get(key) || 0);
    });
  }

  /* ── Jump to a question card (used by "My activity" lists) ── */
  function jumpToQuestion(rec) {
    if (!rec || !rec.el) return;
    closeActivityModal();
    rec.el.hidden = false;
    const repliesEl = rec.el.querySelector('.forum-replies');
    if (repliesEl) repliesEl.hidden = false;
    rec.el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    rec.el.classList.add('forum-q-highlight');
    setTimeout(() => rec.el.classList.remove('forum-q-highlight'), 1500);
  }

  function buildActivityListItem(rec) {
    const li = document.createElement('li');
    li.className = 'forum-my-activity-item';
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = rec.title || 'Untitled question';
    btn.addEventListener('click', () => jumpToQuestion(rec));
    li.appendChild(btn);
    return li;
  }

  /* ── Stats ── */
  function recomputeStats() {
    if (statQuestions) statQuestions.textContent = String(questionRecords.size);
    if (statAnswers) {
      let total = 0;
      answerCounts.forEach((v) => (total += v));
      statAnswers.textContent = String(total);
    }

    if (customer.loggedIn) {
      const myQuestionRecs = [];
      const myAnsweredRecs = [];
      questionRecords.forEach((rec, qid) => {
        if (rec.authorId === customer.id) myQuestionRecs.push(rec);
        if ((myAnswerCounts.get(qid) || 0) > 0) myAnsweredRecs.push(rec);
      });
      myQuestionRecs.sort((a, b) => b.createdMs - a.createdMs);
      myAnsweredRecs.sort((a, b) => b.createdMs - a.createdMs);

      if (statMyQuestions) statMyQuestions.textContent = String(myQuestionRecs.length);
      if (statMyAnswers) {
        let myA = 0;
        myAnswerCounts.forEach((v) => (myA += v));
        statMyAnswers.textContent = String(myA);
      }

      if (myQuestionsList) {
        myQuestionsList.innerHTML = '';
        if (myQuestionRecs.length === 0) {
          myQuestionsList.innerHTML = '<li class="forum-my-activity-empty">Nothing yet</li>';
        } else {
          myQuestionRecs
            .slice(0, MY_ACTIVITY_SIDEBAR_LIMIT)
            .forEach((rec) => myQuestionsList.appendChild(buildActivityListItem(rec)));
        }
      }

      if (myAnswersList) {
        myAnswersList.innerHTML = '';
        if (myAnsweredRecs.length === 0) {
          myAnswersList.innerHTML = '<li class="forum-my-activity-empty">Nothing yet</li>';
        } else {
          myAnsweredRecs
            .slice(0, MY_ACTIVITY_SIDEBAR_LIMIT)
            .forEach((rec) => myAnswersList.appendChild(buildActivityListItem(rec)));
        }
      }

      // Full lists (shown in "View all" modal)
      if (myQuestionsListFull) {
        myQuestionsListFull.innerHTML = '';
        if (myQuestionRecs.length === 0) {
          myQuestionsListFull.innerHTML = '<li class="forum-my-activity-empty">Nothing yet</li>';
        } else {
          myQuestionRecs.forEach((rec) => myQuestionsListFull.appendChild(buildActivityListItem(rec)));
        }
      }
      if (myAnswersListFull) {
        myAnswersListFull.innerHTML = '';
        if (myAnsweredRecs.length === 0) {
          myAnswersListFull.innerHTML = '<li class="forum-my-activity-empty">Nothing yet</li>';
        } else {
          myAnsweredRecs.forEach((rec) => myAnswersListFull.appendChild(buildActivityListItem(rec)));
        }
      }

    }

    updateCategoryCounts();
  }

  /* ── Voting ── */
  async function toggleVote(refPath, field, currentData) {
    if (!customer.loggedIn) { goToLogin(); return; }
    const targetRef = doc(db, ...refPath);
    const opposite  = field === 'likes' ? 'dislikes' : 'likes';
    const isActive  = (currentData[field] || []).includes(customer.id);
    const updates   = {};
    updates[field]   = isActive ? arrayRemove(customer.id) : arrayUnion(customer.id);
    if (!isActive)   updates[opposite] = arrayRemove(customer.id);
    try {
      await updateDoc(targetRef, updates);
    } catch (err) {
      console.error('Vote failed', err);
      showToast(firestoreErrorMessage(err, "Couldn't save your vote, try again"));
    }
  }

  function voteInlineEl(refPath, data) {
    const likes    = data.likes    || [];
    const dislikes = data.dislikes || [];
    const liked    = customer.loggedIn && likes.includes(customer.id);
    const disliked = customer.loggedIn && dislikes.includes(customer.id);
    const likeCount    = likes.length;
    const dislikeCount = dislikes.length;
    const score    = likeCount - dislikeCount;

    const wrap = document.createElement('div');
    wrap.className = 'forum-vote-inline';
    wrap.innerHTML = `
      <button type="button" class="forum-vote-btn forum-vote-up" aria-pressed="${liked}" aria-label="Like">▲</button>
      <span class="forum-vote-count forum-vote-count-up">${likeCount}</span>
      <button type="button" class="forum-vote-btn forum-vote-down" aria-pressed="${disliked}" aria-label="Dislike">▼</button>
      <span class="forum-vote-count forum-vote-count-down">${dislikeCount}</span>
    `;
    wrap.querySelector('.forum-vote-up').addEventListener('click', () => toggleVote(refPath, 'likes', data));
    wrap.querySelector('.forum-vote-down').addEventListener('click', () => toggleVote(refPath, 'dislikes', data));
    return { el: wrap, score };
  }

  /* ── Render reply ── */
  function renderReply(questionId, reply) {
    const created = reply.data.createdAt && reply.data.createdAt.toDate ? reply.data.createdAt.toDate() : null;
    const row = document.createElement('div');
    row.className = 'forum-reply';

    row.appendChild(avatarEl(reply.data.authorName || 'Customer', 28));

    const body = document.createElement('div');
    body.className = 'forum-reply-body';

    const header = document.createElement('div');
    header.className = 'forum-reply-header';
    header.innerHTML = `
      <span class="forum-reply-author">${escapeHtml(reply.data.authorName || 'Customer')}</span>
      <span class="forum-q-dot">·</span>
      <span>${timeAgo(created)}</span>
    `;

    const text = document.createElement('p');
    text.className = 'forum-reply-text';
    text.textContent = reply.data.text;

    const { el: voteRow } = voteInlineEl([collectionName, questionId, 'replies', reply.id], reply.data);
    voteRow.classList.add('forum-reply-vote');

    body.append(header, text, voteRow);
    row.appendChild(body);
    return row;
  }

  /* ── Watch replies ── */
  function watchReplies(questionId, repliesContainer, answersBtn, rec) {
    const q = query(collection(db, collectionName, questionId, 'replies'), orderBy('createdAt', 'asc'));
    onSnapshot(
      q,
      (snap) => {
        repliesContainer.innerHTML = '';
        let count = 0;
        let myCount = 0;
        snap.forEach((ds) => {
          const data = ds.data();
          repliesContainer.appendChild(renderReply(questionId, { id: ds.id, data }));
          count++;
          if (customer.loggedIn && data.authorId === customer.id) myCount++;
        });
        answerCounts.set(questionId, count);
        myAnswerCounts.set(questionId, myCount);
        rec.answerCount = count;

        answersBtn.textContent = count === 0 ? '0 Answers' : count === 1 ? '1 Answer' : `${count} Answers`;
        answersBtn.className   = `forum-answers-btn${count > 0 ? ' has-answers' : ''}`;

        // Unanswered badge
        const badge = answersBtn.nextElementSibling;
        if (badge && badge.classList.contains('forum-unanswered-badge')) {
          badge.hidden = count > 0;
        }

        recomputeStats();
        applyFilters();
      },
      (err) => {
        // A replies listener failing (e.g. permission-denied on this
        // subcollection) previously failed silently, which is exactly
        // what made "other users can't see/post answers" invisible.
        console.error('Replies load failed', err);
        showToast(firestoreErrorMessage(err, "Couldn't load answers for this question"));
      }
    );
  }

  /* ── Render question card ── */
  function renderQuestion(question) {
    const d        = question.data;
    const created  = d.createdAt && d.createdAt.toDate ? d.createdAt.toDate() : null;
    const title    = d.title || d.text || 'Untitled question';
    const bodyText = d.title ? (d.text && d.text !== d.title ? d.text : '') : '';

    const card = document.createElement('article');
    card.className  = 'forum-question';
    card.setAttribute('role', 'listitem');
    card.dataset.qid = question.id;

    // Title
    const titleEl = document.createElement('h3');
    titleEl.className = 'forum-q-title';
    const titleBtn = document.createElement('button');
    titleBtn.type      = 'button';
    titleBtn.className = 'forum-q-title-btn';
    titleBtn.textContent = title;
    titleEl.appendChild(titleBtn);

    // Meta
    const metaEl = document.createElement('div');
    metaEl.className = 'forum-q-meta';
    metaEl.innerHTML = `
      <span class="forum-q-author">${escapeHtml(d.authorName || 'Customer')}</span>
      <span class="forum-q-dot">·</span>
      <span>${timeAgo(created)}</span>
      ${d.categoryLabel ? `<span class="forum-q-dot">·</span><span class="forum-q-cat">${escapeHtml(d.categoryIcon || '')} ${escapeHtml(d.categoryLabel)}</span>` : ''}
    `;

    // Body preview (only if we have a separate body field)
    let previewEl = null;
    if (bodyText && bodyText.trim()) {
      previewEl = document.createElement('p');
      previewEl.className  = 'forum-q-preview';
      previewEl.textContent = bodyText;
    }

    // Divider
    const hr = document.createElement('hr');
    hr.className = 'forum-q-divider';

    // Footer
    const footer = document.createElement('div');
    footer.className = 'forum-q-footer';

    const { el: voteEl, score } = voteInlineEl([collectionName, question.id], d);

    const answersBtn = document.createElement('button');
    answersBtn.type      = 'button';
    answersBtn.className = 'forum-answers-btn';
    answersBtn.textContent = '0 Answers';

    const unanswBadge = document.createElement('span');
    unanswBadge.className = 'forum-unanswered-badge';
    unanswBadge.textContent = 'Unanswered';

    const answerBtn = document.createElement('button');
    answerBtn.type      = 'button';
    answerBtn.className = 'forum-btn forum-btn--secondary forum-answer-btn';
    answerBtn.style.cssText = 'font-size:13px;padding:6px 14px;';
    answerBtn.textContent = 'Answer';

    footer.append(voteEl, answersBtn, unanswBadge, answerBtn);

    // Replies container
    const repliesEl = document.createElement('div');
    repliesEl.className = 'forum-replies';
    repliesEl.hidden    = true;

    // Reply form
    const replyForm = document.createElement('form');
    replyForm.className = 'forum-reply-form';
    replyForm.innerHTML = `
      <textarea class="forum-reply-textarea" placeholder="Write your answer…" maxlength="2000" required></textarea>
      <div class="forum-reply-form-row">
        <button type="button" class="forum-btn forum-btn--secondary forum-reply-cancel-btn" style="font-size:13px;padding:6px 14px;">Cancel</button>
        <button type="submit" class="forum-btn forum-btn--primary forum-reply-submit-btn" style="font-size:13px;padding:6px 14px;">Post answer</button>
      </div>
    `;

    // Assemble card
    const children = [titleEl, metaEl];
    if (previewEl) children.push(previewEl);
    children.push(hr, footer, repliesEl, replyForm);
    card.append(...children);

    // Record for filter/sort
    const rec = {
      el:          card,
      searchText:  `${title} ${bodyText} ${d.authorName || ''} ${d.categoryLabel || ''}`,
      categoryId:  d.categoryId || null,
      voteScore:   score,
      createdMs:   created ? created.getTime() : 0,
      answerCount: 0,
      authorId:    d.authorId || null,
      title:       title
    };

    /* ── Events ── */
    // Title click → expand/collapse replies
    titleBtn.addEventListener('click', () => {
      repliesEl.hidden = !repliesEl.hidden;
    });

    // Answer count click → toggle replies
    answersBtn.addEventListener('click', () => {
      repliesEl.hidden = !repliesEl.hidden;
    });

    // Answer button → open reply form
    answerBtn.addEventListener('click', () => {
      if (!customer.loggedIn) { goToLogin(); return; }
      const isOpen = replyForm.classList.contains('is-open');
      replyForm.classList.toggle('is-open', !isOpen);
      if (!isOpen) {
        repliesEl.hidden = false;
        replyForm.querySelector('.forum-reply-textarea').focus();
      }
    });

    // Cancel reply
    replyForm.querySelector('.forum-reply-cancel-btn').addEventListener('click', () => {
      replyForm.classList.remove('is-open');
      replyForm.reset();
    });

    // Submit reply
    replyForm.addEventListener('submit', async (ev) => {
      ev.preventDefault();
      if (!customer.loggedIn) { goToLogin(); return; }
      const textarea  = replyForm.querySelector('.forum-reply-textarea');
      const submitBtn = replyForm.querySelector('.forum-reply-submit-btn');
      const text = textarea.value.trim();
      if (!text) return;
      submitBtn.disabled = true;
      try {
        await addDoc(collection(db, collectionName, question.id, 'replies'), {
          text,
          authorId:   customer.id,
          authorName: customer.name || 'Customer',
          createdAt:  serverTimestamp(),
          likes:    [],
          dislikes: []
        });
        textarea.value = '';
        replyForm.classList.remove('is-open');
        showToast('Your answer was posted');
      } catch (err) {
        console.error('Reply failed', err);
        showToast(firestoreErrorMessage(err, 'Failed to post. Please try again.'));
      } finally {
        submitBtn.disabled = false;
      }
    });

    watchReplies(question.id, repliesEl, answersBtn, rec);

    return { card, rec };
  }

  /* ── Live Firestore listener ── */
  const questionsQuery = query(collection(db, collectionName), orderBy('createdAt', 'desc'));

  let didHashJump = false;
  function jumpFromHashIfNeeded() {
    if (didHashJump) return;
    const m = /^#question-(.+)$/.exec(window.location.hash);
    if (!m) return;
    const rec = questionRecords.get(decodeURIComponent(m[1]));
    if (rec) {
      didHashJump = true;
      setTimeout(() => jumpToQuestion(rec), 50);
    }
  }

  onSnapshot(
    questionsQuery,
    (snap) => {
      questionRecords.clear();
      if (snap.empty) {
        listEl.innerHTML = `<div class="forum-empty-state">No questions yet. Be the first to ask!</div>`;
        recomputeStats();
        return;
      }
      listEl.innerHTML = '';
      snap.forEach((ds) => {
        const { card, rec } = renderQuestion({ id: ds.id, data: ds.data() });
        listEl.appendChild(card);
        questionRecords.set(ds.id, rec);
      });
      recomputeStats();
      applyFilters();
      jumpFromHashIfNeeded();
    },
    (err) => {
      console.error('Forum load failed', err);
      listEl.innerHTML = `<div class="forum-empty-state">${firestoreErrorMessage(err, 'Could not load questions right now. Please refresh.')}</div>`;
    }
  );
});
